// ⌘K command palette — filter, arrow keys, enter. Opens via the keyboard
// shortcut or a window 'palette:open' event (dispatched by the topbar button).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface PaletteAction {
  id: string;
  label: string;
  hint: string;
  run: () => void;
}

export const OPEN_PALETTE_EVENT = 'palette:open';

function CommandPalette({ actions }: { actions: readonly PaletteAction[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setCursor(0);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
        setQuery('');
        setCursor(0);
      } else if (e.key === 'Escape') {
        close();
      }
    };
    const onOpen = () => {
      setOpen(true);
      setQuery('');
      setCursor(0);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN_PALETTE_EVENT, onOpen);
    };
  }, [close]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return actions;
    return actions.filter(
      (a) => a.label.toLowerCase().includes(q) || a.hint.toLowerCase().includes(q),
    );
  }, [actions, query]);

  if (!open) return null;

  const runAction = (action: PaletteAction) => {
    close();
    action.run();
  };

  const onInputKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, matches.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === 'Enter') {
      const action = matches[Math.min(cursor, matches.length - 1)];
      if (action) runAction(action);
    }
  };

  return (
    <div className="palette-backdrop" onClick={close} role="presentation">
      <div
        className="palette"
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="palette-input-row">
          <span className="palette-prompt" aria-hidden>
            ↳
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setCursor(0);
            }}
            onKeyDown={onInputKey}
            placeholder="type a command…"
            aria-label="Search commands"
            spellCheck={false}
          />
          <kbd>esc</kbd>
        </div>
        <ul className="palette-list" role="listbox">
          {matches.length === 0 && <li className="palette-empty">nothing matches — try “theme”</li>}
          {matches.map((a, i) => (
            <li key={a.id} role="option" aria-selected={i === cursor}>
              <button
                type="button"
                className={i === cursor ? 'is-active' : ''}
                onMouseEnter={() => setCursor(i)}
                onClick={() => runAction(a)}
              >
                <span>{a.label}</span>
                <span className="palette-hint">{a.hint}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default CommandPalette;
